/**
 * Route table for the kiosk SPA.
 *
 * Lazy-loaded per screen so the initial bundle stays small — each screen
 * becomes its own chunk. The auth guard for non-/login routes lives in
 * `KioskShell`, which wraps every route.
 */

import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ROUTES } from './routes';

type ScreenModule = { default?: ComponentType };

/** Wrap a named export from a screen module so React.lazy can load it. */
function lazyNamed<K extends string>(
  loader: () => Promise<Record<K, ComponentType>>,
  exportName: K,
): LazyExoticComponent<ComponentType> {
  return lazy(async () => {
    const mod = await loader();
    const component = mod[exportName] ?? (mod as ScreenModule).default;
    if (!component) {
      throw new Error(`Screen module is missing expected export "${String(exportName)}"`);
    }
    return { default: component };
  });
}

// ─── Lazy screen registry ────────────────────────────────────────────────────

const LoginScreen = lazyNamed(() => import('../screens/LoginScreen'), 'LoginScreen');
const WelcomeScreen = lazyNamed(() => import('../screens/WelcomeScreen'), 'WelcomeScreen');
const ScanScreen = lazyNamed(() => import('../screens/ScanScreen'), 'ScanScreen');
const BalanceScreen = lazyNamed(() => import('../screens/BalanceScreen'), 'BalanceScreen');
const TopUpScreen = lazyNamed(() => import('../screens/TopUpScreen'), 'TopUpScreen');
const PurchaseScreen = lazyNamed(() => import('../screens/PurchaseScreen'), 'PurchaseScreen');
const HistoryScreen = lazyNamed(() => import('../screens/HistoryScreen'), 'HistoryScreen');
const ErrorScreen = lazyNamed(() => import('../screens/ErrorScreen'), 'ErrorScreen');
const SuccessScreen = lazyNamed(() => import('../screens/SuccessScreen'), 'SuccessScreen');

const FnbMenuScreen = lazyNamed(() => import('../screens/FnbMenuScreen'), 'FnbMenuScreen');
const FnbCartScreen = lazyNamed(() => import('../screens/FnbCartScreen'), 'FnbCartScreen');
const FnbOrderConfirmScreen = lazyNamed(
  () => import('../screens/FnbOrderConfirmScreen'),
  'FnbOrderConfirmScreen',
);
const FnbOrderStatusScreen = lazyNamed(
  () => import('../screens/FnbOrderStatusScreen'),
  'FnbOrderStatusScreen',
);

const InfoHomeScreen = lazyNamed(() => import('../screens/InfoHomeScreen'), 'InfoHomeScreen');
const InfoMapScreen = lazyNamed(() => import('../screens/InfoMapScreen'), 'InfoMapScreen');
const InfoWaitTimesScreen = lazyNamed(
  () => import('../screens/InfoWaitTimesScreen'),
  'InfoWaitTimesScreen',
);
const InfoShowScheduleScreen = lazyNamed(
  () => import('../screens/InfoShowScheduleScreen'),
  'InfoShowScheduleScreen',
);
const InfoWristbandScreen = lazyNamed(
  () => import('../screens/InfoWristbandScreen'),
  'InfoWristbandScreen',
);

const PhotoGalleryScreen = lazyNamed(
  () => import('../screens/PhotoGalleryScreen'),
  'PhotoGalleryScreen',
);
const PhotoCartScreen = lazyNamed(() => import('../screens/PhotoCartScreen'), 'PhotoCartScreen');
const PhotoPaymentScreen = lazyNamed(
  () => import('../screens/PhotoPaymentScreen'),
  'PhotoPaymentScreen',
);
const PhotoReceiptScreen = lazyNamed(
  () => import('../screens/PhotoReceiptScreen'),
  'PhotoReceiptScreen',
);

const WalkUpIdentityScreen = lazyNamed(
  () => import('../screens/WalkUpIdentityScreen'),
  'WalkUpIdentityScreen',
);
const WalkUpEntryScreen = lazyNamed(
  () => import('../screens/WalkUpEntryScreen'),
  'WalkUpEntryScreen',
);
const WalkUpAttractionsScreen = lazyNamed(
  () => import('../screens/WalkUpAttractionsScreen'),
  'WalkUpAttractionsScreen',
);
const WalkUpOverviewScreen = lazyNamed(
  () => import('../screens/WalkUpOverviewScreen'),
  'WalkUpOverviewScreen',
);
const WalkUpSuccessScreen = lazyNamed(
  () => import('../screens/WalkUpSuccessScreen'),
  'WalkUpSuccessScreen',
);

const WristbandScanScreen = lazyNamed(
  () => import('../screens/WristbandScanScreen'),
  'WristbandScanScreen',
);
const WristbandProfileScreen = lazyNamed(
  () => import('../screens/WristbandProfileScreen'),
  'WristbandProfileScreen',
);
const WristbandAddonPickerScreen = lazyNamed(
  () => import('../screens/WristbandAddonPickerScreen'),
  'WristbandAddonPickerScreen',
);
const WristbandAddonOverviewScreen = lazyNamed(
  () => import('../screens/WristbandAddonOverviewScreen'),
  'WristbandAddonOverviewScreen',
);
const WristbandAddonSuccessScreen = lazyNamed(
  () => import('../screens/WristbandAddonSuccessScreen'),
  'WristbandAddonSuccessScreen',
);
const WristbandTransferSelectScreen = lazyNamed(
  () => import('../screens/WristbandTransferSelectScreen'),
  'WristbandTransferSelectScreen',
);
const WristbandTransferRecipientScreen = lazyNamed(
  () => import('../screens/WristbandTransferRecipientScreen'),
  'WristbandTransferRecipientScreen',
);
const WristbandTransferConfirmScreen = lazyNamed(
  () => import('../screens/WristbandTransferConfirmScreen'),
  'WristbandTransferConfirmScreen',
);
const WristbandTransferSuccessScreen = lazyNamed(
  () => import('../screens/WristbandTransferSuccessScreen'),
  'WristbandTransferSuccessScreen',
);

const StaffAssistanceScreen = lazyNamed(
  () => import('../screens/StaffAssistanceScreen'),
  'StaffAssistanceScreen',
);

// ─── Loading fallback ────────────────────────────────────────────────────────

function RouteFallback(): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center text-white/60">
      <div className="spinner" />
    </div>
  );
}

// ─── Route table ─────────────────────────────────────────────────────────────

export function AppRoutes(): JSX.Element {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path={ROUTES.login} element={<LoginScreen />} />
        <Route path={ROUTES.root} element={<WelcomeScreen />} />
        <Route path={ROUTES.scan} element={<ScanScreen />} />
        <Route path={ROUTES.balance} element={<BalanceScreen />} />
        <Route path={ROUTES.topUp} element={<TopUpScreen />} />
        <Route path={ROUTES.purchase} element={<PurchaseScreen />} />
        <Route path={ROUTES.history} element={<HistoryScreen />} />
        <Route path={ROUTES.error} element={<ErrorScreen />} />
        <Route path={ROUTES.success} element={<SuccessScreen />} />

        <Route path={ROUTES.fnb.menu} element={<FnbMenuScreen />} />
        <Route path={ROUTES.fnb.cart} element={<FnbCartScreen />} />
        <Route path={ROUTES.fnb.orderConfirm} element={<FnbOrderConfirmScreen />} />
        <Route path={ROUTES.fnb.orderStatus} element={<FnbOrderStatusScreen />} />

        <Route path={ROUTES.info.home} element={<InfoHomeScreen />} />
        <Route path={ROUTES.info.map} element={<InfoMapScreen />} />
        <Route path={ROUTES.info.waitTimes} element={<InfoWaitTimesScreen />} />
        <Route path={ROUTES.info.shows} element={<InfoShowScheduleScreen />} />
        <Route path={ROUTES.info.wristband} element={<InfoWristbandScreen />} />

        <Route path={ROUTES.photos.gallery} element={<PhotoGalleryScreen />} />
        <Route path={ROUTES.photos.cart} element={<PhotoCartScreen />} />
        <Route path={ROUTES.photos.payment} element={<PhotoPaymentScreen />} />
        <Route path={ROUTES.photos.receipt} element={<PhotoReceiptScreen />} />

        <Route path={ROUTES.walkUp.identity} element={<WalkUpIdentityScreen />} />
        <Route path={ROUTES.walkUp.tickets} element={<WalkUpEntryScreen />} />
        <Route path={ROUTES.walkUp.attractions} element={<WalkUpAttractionsScreen />} />
        <Route path={ROUTES.walkUp.overview} element={<WalkUpOverviewScreen />} />
        <Route path={ROUTES.walkUp.success} element={<WalkUpSuccessScreen />} />

        {/* Wristband self-service: scan → profile → addon / F&B. Distinct from
            /scan (print-only) so print and addon flows never cross-contaminate. */}
        <Route path={ROUTES.wristband.scan} element={<WristbandScanScreen />} />
        <Route path={ROUTES.wristband.profile} element={<WristbandProfileScreen />} />
        <Route path={ROUTES.wristband.addon} element={<WristbandAddonPickerScreen />} />
        <Route path={ROUTES.wristband.addonOverview} element={<WristbandAddonOverviewScreen />} />
        <Route path={ROUTES.wristband.addonSuccess} element={<WristbandAddonSuccessScreen />} />
        <Route path={ROUTES.wristband.transfer} element={<WristbandTransferSelectScreen />} />
        <Route path={ROUTES.wristband.transferRecipient} element={<WristbandTransferRecipientScreen />} />
        <Route path={ROUTES.wristband.transferConfirm} element={<WristbandTransferConfirmScreen />} />
        <Route path={ROUTES.wristband.transferSuccess} element={<WristbandTransferSuccessScreen />} />

        <Route path={ROUTES.staffAssistance} element={<StaffAssistanceScreen />} />

        <Route path="*" element={<Navigate to={ROUTES.root} replace />} />
      </Routes>
    </Suspense>
  );
}
